import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import {QRCodeSVG} from "qrcode.react";
import * as React from "react";
import {useEffect, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    BodyMonospace,
    HeadingSmall,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {doNotTranslate} from "@khan/i18n";

import type {
    enableTotpTwoFactor,
    enableTotpTwoFactorVariables,
} from "./__graphql-types__/enableTotpTwoFactor.ts";
import type {
    generateTotpTwoFactorSecret,
    generateTotpTwoFactorSecretVariables,
} from "./__graphql-types__/generateTotpTwoFactorSecret.ts";
import {RecoveryCodesTwoFactorModal} from "./recovery-codes-two-factor-modal.tsx";

type Props = {
    /* The email of the user, used to identify the account in the user's
    authenticator app. */
    email: string;
    /* A function to call when the user has successfully changed their settings.
    Pops up a toast and refreshes the page. */
    onSettingsChanged: () => unknown;
};

export const GENERATE_2FA_SECRET_MUTATION: GqlOperation<
    generateTotpTwoFactorSecret,
    generateTotpTwoFactorSecretVariables
> = gqlOp(gql`
    mutation generateTotpTwoFactorSecret($email: String!) {
        generateTotpTwoFactorSecret(email: $email) {
            totpSecret
            totpProvisioningUri
            error {
                code
            }
        }
    }
`);

export const ENABLE_2FA_MUTATION: GqlOperation<
    enableTotpTwoFactor,
    enableTotpTwoFactorVariables
> = gqlOp(gql`
    mutation enableTotpTwoFactor($secret: String!, $code: String!) {
        enableTotpTwoFactor(secret: $secret, code: $code) {
            error {
                code
            }
        }
    }
`);

function QrCode({result}): React.ReactElement {
    if (result?.status !== "success") {
        return <CircularSpinner size="large" />;
    }
    if (result.data.generateTotpTwoFactorSecret?.error) {
        return (
            <Body style={styles.errorText}>
                {t`Oops! Something went wrong. Please try again.`}
            </Body>
        );
    }
    return (
        <View>
            <QRCodeSVG
                value={
                    result.data.generateTotpTwoFactorSecret?.totpProvisioningUri
                }
                size={256}
            />
            <Strut size={spacing.small_12} />
            <LabelSmall style={styles.secondaryText}>
                {doNotTranslate`or enter the code manually`}
            </LabelSmall>
            <Strut size={spacing.xSmall_8} />
            <BodyMonospace style={styles.secondaryText}>
                {result.data.generateTotpTwoFactorSecret?.totpSecret}
            </BodyMonospace>
        </View>
    );
}

// NOTE: This is a WIP modal, behind a feature flag ("two_factor_authentication")
// and is not enabled for any users.
// This is a modal for enabling time-based one time password (TOTP) two-factor
// authentication which is set up through an authenticator app. This is accessed
// from the SecuritySettings component in the account settings page. It operates
// by first: generating a secret for the user, which is used to render a QR
// code. Then, the user scans the QR code with their authenticator app, and
// enters a verification code from the app into the modal. Then a mutation is
// called to check the code and enable 2FA for the user. If the code is valid,
// the user is shown their recovery codes (by rendering RecoveryCodesTwoFactorModal).
export function EnableTotpTwoFactorModal({
    email,
    onSettingsChanged,
}: Props): React.ReactElement {
    const [verificationCode, setVerificationCode] = useState("");
    const [error, setError] = useState("");
    type Step = "enable" | "recoveryCodes";
    const [modalStep, setModalStep] = useState<Step>("enable");

    const [generateTotpTwoFactorSecret, generateResult] = useMutation(
        GENERATE_2FA_SECRET_MUTATION,
    );
    const [enableTotpTwoFactorMutation, enableResult] =
        useMutation(ENABLE_2FA_MUTATION);

    // On first render, generate a secret for the user so that we can show the
    // QR code.
    useEffect(() => {
        generateTotpTwoFactorSecret({email});
    }, [email, generateTotpTwoFactorSecret]);

    const secret =
        generateResult?.status === "success" &&
        generateResult?.data.generateTotpTwoFactorSecret?.totpSecret;
    const isLoading =
        enableResult?.status === "loading" ||
        enableResult?.status === "no-data";

    if (modalStep === "recoveryCodes") {
        return (
            <RecoveryCodesTwoFactorModal
                onSettingsChanged={onSettingsChanged}
                regenerating={false}
            />
        );
    }
    const onSubmit = async () => {
        const response = await enableTotpTwoFactorMutation({
            secret: secret || "",
            // There should only be at most 1 whitespace anyways
            code: verificationCode.replace(" ", ""),
        });
        if (response.status === "error") {
            setError(t`Oops! Something went wrong. Please try again.`);
        }
        if (response.status === "success") {
            const error = response.data?.enableTotpTwoFactor?.error;
            if (error && error.code === "INVALID_INPUT") {
                setError(doNotTranslate`Invalid code. Please try again.`);
            } else if (error) {
                setError(t`Oops! Something went wrong. Please try again.`);
            } else {
                setModalStep("recoveryCodes");
            }
        }
    };

    return (
        <OnePaneDialog
            title={doNotTranslate`Set up authenticator app`}
            content={
                <View>
                    <Body>
                        {doNotTranslate`Use an authenticator app on your phone such as Google Authenticator or Okta Verify to scan the QR code below and get 2FA verification codes.`}
                    </Body>
                    <Strut size={spacing.small_12} />
                    <HeadingSmall>
                        {doNotTranslate`Scan the QR Code`}
                    </HeadingSmall>
                    <Strut size={spacing.small_12} />
                    <QrCode result={generateResult} />
                    <Strut size={spacing.small_12} />

                    <LabeledTextField
                        style={{width: 256}}
                        label={
                            <HeadingSmall>
                                {doNotTranslate`Verify your code`}
                            </HeadingSmall>
                        }
                        description={doNotTranslate`Enter the 6-digit code from your authenticator app.`}
                        value={verificationCode}
                        onChange={(code) => {
                            // 6 letters + 1 potential space
                            if (code.length <= 7) {
                                setVerificationCode(code);
                            }
                        }}
                        disabled={isLoading || !secret}
                    />
                    {error && <Body style={styles.errorText}>{error}</Body>}
                </View>
            }
            footer={
                <Button
                    onClick={onSubmit}
                    disabled={
                        // We can't enable 2FA if we don't have a secret
                        !secret ||
                        !verificationCode ||
                        verificationCode.replace(" ", "").length !== 6
                    }
                    spinner={isLoading}
                >
                    {t`Submit`}
                </Button>
            }
            style={styles.modal}
        />
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: color.red,
        marginTop: spacing.xxxSmall_4,
    },
    secondaryText: {
        color: color.offBlack64,
    },
    modal: {
        height: "90%",
        maxHeight: 770,
    },
});
